import { types } from 'actions/organisation';
import { searchSuperOrganisationClinicalTrials } from 'services/api';
import { buildQuery, noValue } from 'utils/search';
import { searchId as id } from 'components/common';

// The search area's "Search across" categories are not the CT panel's to reset.
const resetSkipKeys = ['workItemCategories'];

// Clinical Trials tab. Kept fully separate from the shared People/Activity
// filter slice: the CT tab reuses the `leadSponsor` key with different
// semantics, so `clinicalTrialFilters` must not be merged (SCIL-17744).
// Pure mechanical extraction — no behaviour change here.

export const clinicalTrialsReducers = {
  [types.SET_CLINICALTRIALS_SEARCH_REQUEST]: (state, action) => {
    const { organisationId, searchRequest } = action.payload;
    if (searchRequest.res) {
      return {
        ...state,
        clinicalTrialTotals: {
          ...state.clinicalTrialTotals,
          [organisationId]: {
            people: searchRequest.res.distinctSuperResearchersCount,
            emailCount: searchRequest.res.emailCount,
            countries: searchRequest.res.countryCount,
            activities: searchRequest.res.distinctClinicalTrialsCount,
          },
        },
        blockedForClinicalTrialExport: {
          ...state.blockedForClinicalTrialExport,
          [organisationId]: false,
        },
        organisationClinicalTrials: {
          ...state.organisationClinicalTrials,
          [organisationId]: searchRequest.res,
        },
        loadingClinicalTrialData: {
          ...state.loadingClinicalTrialData,
          [organisationId]: false,
        },
        clinicalTrialSearchError: {
          ...state.clinicalTrialSearchError,
          [organisationId]: action.payload.searchRequest?.error,
        },
        clinicalTrialChangedFilters: {
          ...state.clinicalTrialChangedFilters,
          [organisationId]: false,
        },
      };
    }
    return {
      ...state,
      loadingClinicalTrialData: {
        ...state.loadingClinicalTrialData,
        [organisationId]: searchRequest.loading ?? false,
      },
      clinicalTrialSearchError: {
        ...state.clinicalTrialSearchError,
        [organisationId]: action.payload.searchRequest?.error,
      },
      clinicalTrialTotals: {},
      blockedForClinicalTrialExport: {
        ...state.blockedForClinicalTrialExport,
        [organisationId]: false,
      },
      clinicalTrialChangedFilters: {
        ...state.clinicalTrialChangedFilters,
        [organisationId]: false,
      },
    };
  },

  [types.PERFORM_CLINICALTRIALS_SEARCH]: (state, action) => {
    const { body, request } = action.payload;

    const initialPageNumber = 0;

    const pageNumber =
      state.clinicalTrialPageNumbers[body.organisationId] ?? initialPageNumber;

    const [query, highlightQuery, , nestedQueries] = buildQuery(
      state.clinicalTrialFilters[body.organisationId],
      null,
      null
    );

    const leadSponsor =
      state.clinicalTrialFilters[body.organisationId]?.leadSponsor;
    const collaboratorSponsor =
      state.clinicalTrialFilters[body.organisationId]?.collaboratorSponsor;

    const cancelClinicalTrialsSearch = request({
      url: searchSuperOrganisationClinicalTrials(body.organisationId),
      body: {
        id,
        query: `Category:ClinicalTrials AND ${query}`,
        nestedQueries: nestedQueries,
        count: state.clinicalTrialPageSize[body.organisationId] ?? 50,
        from:
          (state.clinicalTrialPageSize[body.organisationId] ?? 50) * pageNumber,
        orderBy:
          state.clinicalTrialSort[body.organisationId]?.field ||
          'FirstPostedDate',
        order:
          state.clinicalTrialSort[body.organisationId]?.direction || 'desc',
        collapseField: 'NctId.keyword',
        isClinicalTrialSearch: true,
        highlightQuery,
        isLeadSponsor: leadSponsor,
        isCollaborator: collaboratorSponsor,
      },
    });

    return {
      ...state,
      cancelClinicalTrialsSearch,
    };
  },
  [types.SET_CLINICALTRIAL_SORT]: (state, action) => {
    const { organisationId, sort } = action.payload;
    return {
      ...state,
      clinicalTrialSort: { ...state.clinicalTrialSort, [organisationId]: sort },
    };
  },
  [types.SET_CLINICALTRIAL_PAGE_NUMBER]: (state, action) => {
    const { organisationId, pageNumber } = action.payload;
    return {
      ...state,
      clinicalTrialPageNumbers: {
        ...state.clinicalTrialPageNumbers,
        [organisationId]: pageNumber,
      },
    };
  },
  [types.SET_CLINICALTRIAL_PAGE_SIZE]: (state, action) => {
    const { organisationId, pageSize } = action.payload;
    return {
      ...state,
      clinicalTrialPageSize: {
        ...state.clinicalTrialPageSize,
        [organisationId]: pageSize,
      },
    };
  },
  [types.SET_CLINICALTRIAL_FILTER_GROUP]: (state, action) => {
    const { organisationId, id, value } = action.payload;
    // Copy the org entry before mutating — see SET_FILTER_GROUP in
    // researchers.js. Filter-count badges subscribe by reference.
    const orgFilters = { ...state.clinicalTrialFilters[organisationId] };
    if (noValue(value) && id !== 'term') {
      delete orgFilters[id];
    } else {
      orgFilters[id] = value;
    }

    return {
      ...state,
      clinicalTrialFilters: {
        ...state.clinicalTrialFilters,
        [organisationId]: orgFilters,
      },
      blockedForClinicalTrialExport: {
        ...state.blockedForClinicalTrialExport,
        [organisationId]: true,
      },
      clinicalTrialChangedFilters: {
        ...state.clinicalTrialChangedFilters,
        [organisationId]: true,
      },
    };
  },
  [types.RESET_CLINICALTRIAL_FILTERS]: (state, action) => {
    const { organisationId } = action.payload;
    let newState = { ...state.clinicalTrialFilters[organisationId] };
    if (noValue(newState)) {
      newState = {};
    } else {
      Object.keys(newState).forEach(key => {
        if (resetSkipKeys.findIndex(k => k === key) < 0) delete newState[key];
      });
    }

    return {
      ...state,
      clinicalTrialFilters: {
        ...state.clinicalTrialFilters,
        [organisationId]: newState,
      },
      blockedForClinicalTrialExport: {
        ...state.blockedForClinicalTrialExport,
        [organisationId]: true,
      },
      clinicalTrialChangedFilters: {
        ...state.clinicalTrialChangedFilters,
        [organisationId]: true,
      },
    };
  },
  [types.CLEAR_CLINICALTRIAL_FILTERS]: (state, action) => {
    const { organisationId } = action.payload;
    let newState = { ...state.clinicalTrialFilters[organisationId] };
    if (noValue(newState)) {
      newState = {};
    } else {
      Object.keys(newState).forEach(key => {
        delete newState[key];
      });
    }
    return {
      ...state,
      clinicalTrialFilters: {
        ...state.clinicalTrialFilters,
        [organisationId]: newState,
      },
      blockedForClinicalTrialExport: {
        ...state.blockedForClinicalTrialExport,
        [organisationId]: true,
      },
      clinicalTrialChangedFilters: {
        ...state.clinicalTrialChangedFilters,
        [organisationId]: true,
      },
    };
  },
  [types.SET_CLINICALTRIAL_FILTERS_BY_STATE_GROUP]: (state, action) => {
    const { organisationId, newState } = action.payload;
    return {
      ...state,
      clinicalTrialFilters: {
        ...state.clinicalTrialFilters,
        [organisationId]: newState,
      },
    };
  },
};
